import { useQuery } from '@tanstack/react-query';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState from '@/components/common/ContentErrorState';
import EmptyState from '@/components/common/EmptyState';
import { queryKeys } from '@/lib/queryKeys';
import {
  listJudgeAgents,
  type JudgeAgent,
} from '@/services/judgeAgentsApi';

export default function AdminJudgeAgentsPage() {
  const agentsQuery = useQuery({
    queryKey: queryKeys.judgeAgents.list,
    queryFn: listJudgeAgents,
    retry: 1,
    refetchInterval: 10_000,
  });

  if (agentsQuery.isLoading) {
    return <PageLoading message="评测 Agent 加载中…" />;
  }

  if (agentsQuery.isError) {
    return (
      <div className="max-w-[980px] mx-auto px-6 py-12">
        <ContentApiErrorState
          error={agentsQuery.error}
          onRetry={() => void agentsQuery.refetch()}
        />
      </div>
    );
  }

  const agents = agentsQuery.data ?? [];

  return (
    <div className="max-w-[980px] mx-auto px-6 py-12 space-y-6">
      <div>
        <h1 className="font-semibold text-apple-near-black text-[2rem] leading-tight">
          评测 Agent
        </h1>
        <p className="text-apple-gray text-[0.9375rem] mt-1">
          Agent 在内网/局域网机器主动连接后端并领取评测任务。
        </p>
      </div>

      <section className="bg-white rounded-2xl p-5 shadow-card-sm space-y-3">
        <p className="text-[0.875rem] text-apple-near-black">
          请在后端环境中配置 <code>JUDGE_AGENT_SHARED_TOKEN</code>，并在本地 Agent 使用相同 token。
        </p>
        <pre className="bg-apple-light-gray rounded-lg p-3 text-[0.8125rem] text-apple-near-black overflow-auto">
{`CODEPILOT_BACKEND_URL=https://<backend-domain>/api \\
JUDGE_AGENT_TOKEN=<token> \\
JUDGE_AGENT_NAME=local-judge \\
npm run start -w apps/judge-service`}
        </pre>
      </section>

      <section className="space-y-3">
        {agents.length === 0 ? (
          <EmptyState title="暂无在线或已注册 Agent" description="启动 Agent 后将在这里显示状态。" />
        ) : (
          agents.map((agent) => <JudgeAgentCard key={agent.id} agent={agent} />)
        )}
      </section>
    </div>
  );
}

function JudgeAgentCard({ agent }: { agent: JudgeAgent }) {
  const statusTone =
    agent.status === 'ONLINE'
      ? 'bg-green-50 text-green-700'
      : 'bg-red-50 text-red-700';

  return (
    <article className="bg-white rounded-2xl p-5 shadow-card-sm space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold text-[1.0625rem] text-apple-near-black">{agent.name}</h2>
        <span className={`rounded-full px-2 py-0.5 text-[0.75rem] ${statusTone}`}>
          {agent.status}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[0.875rem] text-apple-gray">
        <div>运行中任务: {agent.runningJobs}</div>
        <div>最大并发: {agent.capabilities.maxConcurrency}</div>
        <div>语言: {agent.capabilities.languages.join(', ') || '-'}</div>
        <div>判题器: {agent.capabilities.checkers.join(', ') || '-'}</div>
        <div>
          最近心跳: {agent.lastHeartbeatAt ? new Date(agent.lastHeartbeatAt).toLocaleString() : '-'}
        </div>
        <div>更新时间: {new Date(agent.updatedAt).toLocaleString()}</div>
      </div>
    </article>
  );
}
